import React, { useEffect, useRef, useState } from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Box,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';

import { Search } from '@mui/icons-material';

import fetchPageData from '../helpers/fetchPageData';
import { useGridSorting } from '../hooks/useGridSorting';
import { IDataGridColumn } from '../interfaces/IDataGridColumn';
import ExpandableTableCell from './ExpandableTableCell';

interface IKeyValMap {
  [key: string]: any;
}

type TColumnsResolver = (
  col: IDataGridColumn,
  row: IKeyValMap,
) => React.ReactNode | undefined;

interface GridViewProps {
  rows: IKeyValMap[];
  columns: IDataGridColumn[];
  columnsResolver?: TColumnsResolver;
  expandableRows?: boolean;
}

export const GridView: React.FC<GridViewProps> = ({
  rows,
  columns,
  columnsResolver,
  expandableRows = false,
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [rowsExceedingHeight, setRowsExceedingHeight] = useState<Set<number>>(
    new Set(),
  );
  const gridRef = useRef<HTMLDivElement>(null);

  const { sortedRows, setSortState, updateRowsRefs, getSortIcon } =
    useGridSorting(rows, columns);

  useEffect(() => {
    if (rows.length) {
      updateRowsRefs(rows);
    }
  }, [rows]);

  const handleSortRequest = async (
    ev: React.MouseEvent<
      HTMLButtonElement | HTMLAnchorElement | HTMLSpanElement
    >,
  ) => {
    await fetchPageData(ev, columns, setSortState, updateRowsRefs);
  };

  const handleExpandClick = (index: number) => {
    setExpandedRows(prev => {
      const updated = new Set(prev);
      updated.has(index) ? updated.delete(index) : updated.add(index);
      return updated;
    });
  };

  const handleContentExceedsHeight = (rowIndex: number, exceeds: boolean) => {
    setRowsExceedingHeight(prev => {
      const updated = new Set(prev);
      if (exceeds) {
        updated.add(rowIndex);
      } else {
        updated.delete(rowIndex);
      }
      return updated;
    });
  };

  const renderCellContent = (col: IDataGridColumn, row: IKeyValMap) => {
    const resolvedValue = columnsResolver
      ? columnsResolver(col, row)
      : undefined;
    const cellValue =
      resolvedValue !== undefined ? resolvedValue : row[col.name];

    return cellValue;
  };

  return (
    <Box sx={{ display: 'flex' }} ref={gridRef}>
      <TableContainer
        component={Paper}
        sx={{ flex: '1 1 auto', overflowX: 'auto' }}
      >
        <Table
          stickyHeader
          sx={{
            minWidth: {
              xs: `calc(${columns.length} * 150px)`,
              xl: `calc(${columns.length} * 7vw)`,
            },
          }}
        >
          <TableHead>
            <TableRow>
              {columns.map(col => (
                <TableCell
                  key={col.name}
                  sx={{
                    minWidth: col.flexBasis || '150px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {col.sortable ? (
                    <TableSortLabel
                      active={
                        !!sortedRows.find(row => row[col.name] !== undefined)
                      }
                      direction={
                        sortedRows.find(row => row[col.name] !== undefined)
                          ? 'asc'
                          : 'desc'
                      }
                      onClick={ev => handleSortRequest(ev)}
                      data-key={col.name}
                      IconComponent={() => getSortIcon(col.name)}
                    >
                      {col.labelCopy?.toUpperCase() || col.name.toUpperCase()}
                    </TableSortLabel>
                  ) : (
                    col.labelCopy?.toUpperCase() || col.name.toUpperCase()
                  )}
                </TableCell>
              ))}
              {expandableRows && <TableCell />}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={expandableRows ? columns.length + 1 : columns.length}
                  align="center"
                  sx={{ py: 8 }}
                >
                  <Stack
                    spacing={1}
                    alignItems="center"
                    sx={{ color: 'text.secondary' }}
                  >
                    <Search sx={{ fontSize: '2rem', opacity: 0.7 }} />
                    No data found.
                  </Stack>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, rowIndex) => (
                <React.Fragment key={rowIndex}>
                  <TableRow hover={!expandableRows}>
                    {!expandableRows &&
                      columns.map((col, colIndex) => (
                        <TableCell key={colIndex}>
                          {renderCellContent(col, row)}
                        </TableCell>
                      ))}

                    {expandableRows &&
                      columns.map((col, colIndex) => (
                        <ExpandableTableCell
                          key={colIndex}
                          collapsedHeight={200}
                          isExpanded={expandedRows.has(rowIndex)}
                          onContentExceedsHeight={exceeds =>
                            handleContentExceedsHeight(rowIndex, exceeds)
                          }
                        >
                          {renderCellContent(col, row)}
                        </ExpandableTableCell>
                      ))}
                    {expandableRows && (
                      <TableCell>
                        {rowsExceedingHeight.has(rowIndex) && (
                          <IconButton
                            size="small"
                            aria-label="expand row"
                            onClick={() => handleExpandClick(rowIndex)}
                          >
                            {expandedRows.has(rowIndex) ? (
                              <KeyboardArrowUpIcon />
                            ) : (
                              <KeyboardArrowDownIcon />
                            )}
                          </IconButton>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
